import { useState } from 'react';
import { Icon } from '@iconify/react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/shadcn/popover';
import { cn } from '@/lib/utils';
import type { CalendarField } from '@/services/bookingCalendarService';

const MODES = ['open', 'closed', 'custom'] as const;
const LABELS: Record<(typeof MODES)[number], string> = {
  open: 'Buka',
  closed: 'Tutup',
  custom: 'Custom',
};

interface FieldStatusMenuProps {
  fields: CalendarField[];
  onSelect: (field: CalendarField, mode: CalendarField['availability_mode']) => void;
}

export default function FieldStatusMenu({ fields, onSelect }: FieldStatusMenuProps) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex h-10 items-center gap-2 rounded-full border border-[#E0E6ED] bg-white px-4 text-sm font-semibold text-[#1F1F1F] shadow-sm transition-colors hover:bg-gray-50"
        >
          <span className="h-2 w-2 rounded-full bg-[#0BCF46]" />
          <span>Status Lapangan</span>
          <Icon icon="ph:caret-down" className="h-3.5 w-3.5 text-gray-500" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 rounded-2xl border border-[#E0E6ED] bg-white p-5 shadow-xl sm:w-96">
        <h3 className="mb-4 text-base font-semibold text-[#0E1726]">Status Lapangan</h3>

        <div className="space-y-4">
          {fields.map((field) => (
            <div key={field.slug}>
              <p className="mb-1.5 text-xs text-gray-500 font-medium">
                {field.name} · {field.sport_name || 'Vinyl · Indoor'}
              </p>
              <div className="flex rounded-full bg-[#F5F6F7] p-1 border border-[#E0E6ED]/60">
                {MODES.map((mode) => {
                  const isActive = field.availability_mode === mode;
                  return (
                    <button
                      key={mode}
                      type="button"
                      className={cn(
                        'flex-1 rounded-full py-1.5 text-xs transition-all text-center',
                        isActive
                          ? 'bg-white font-semibold text-[#FC480D] shadow-sm'
                          : 'font-medium text-gray-500 hover:text-gray-900'
                      )}
                      onClick={() => {
                        onSelect(field, mode);
                        setOpen(false);
                      }}
                    >
                      {LABELS[mode]}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 space-y-2.5 border-t border-[#E0E6ED] pt-3 text-xs leading-relaxed text-gray-600">
          <p>
            <span className="font-semibold text-[#0E1726]">Buka:</span> semua sesi terbuka dan bisa dibooking, berlaku untuk semua tanggal.
          </p>
          <p>
            <span className="font-semibold text-[#0E1726]">Tutup:</span> semua sesi tertutup dan tidak bisa dibooking, berlaku untuk semua tanggal.
          </p>
          <p>
            <span className="font-semibold text-[#0E1726]">Custom:</span> tutup secara default dan diatur per tanggal. Pilih kotak jam di kalender yang akan dibuka, lalu klik Simpan.
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}
